import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StageChip } from "@/components/StageChip";
import { formatINR, banksLabel, fmtLastConv } from "@/lib/format";
import { Loader2, Search, Inbox, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";

// Partner home: "Your cases" only — never a company picker, label or column.
export default function PartnerCases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [bank, setBank] = useState("");
  const [needs, setNeeds] = useState(false);

  useEffect(() => {
    api.get("/partner/home").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => (data?.cases || []).filter((c) => {
    if (stage && c.stage !== stage) return false;
    if (bank && !(c.banks || []).includes(bank)) return false;
    if (needs && !c.needs_my_action) return false;
    if (q && !`${c.case_id} ${c.borrower || ""} ${c.hl_rm || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [data, stage, bank, needs, q]);

  const pill = (on) => `text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"}`;

  return (
    <Layout>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#333333]" data-testid="partner-title">Your cases</h1>
          <p className="text-sm text-[#6B7280]">Welcome, {user?.name}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-[#C5C4C4] absolute left-2.5 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case or borrower" className="pl-8 h-9 w-72 text-sm bg-white border-[#E2E2E2] rounded-lg" data-testid="partner-search" />
        </div>
      </div>

      {data && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-2xl border border-[#FF4802]/30 bg-white p-4 shadow-sm" data-testid="partner-waiting-on-you">
            <div className="flex items-center gap-2 text-sm font-medium text-[#333333]"><Inbox className="w-4 h-4 text-[#FF4802]" /> Waiting on you</div>
            <div className="text-xs text-[#6B7280] mt-1">{data.waiting_on_you.answered_queries.length} answered {data.waiting_on_you.answered_queries.length === 1 ? "query" : "queries"} · {data.waiting_on_you.new_docs} new {data.waiting_on_you.new_docs === 1 ? "document" : "documents"} since your last visit</div>
            {data.waiting_on_you.answered_queries.slice(0, 5).map((qq) => <div key={qq.id} onClick={() => navigate(`/partner/cases/${qq.case_record_id}`)} className="text-xs mt-1 cursor-pointer hover:text-[#FF4802]"><span className="font-mono font-semibold text-[#E2521B]">{qq.case_id}</span> · shared with you: {qq.text.slice(0, 80)}</div>)}
          </div>
          <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm" data-testid="partner-waiting-on-truva">
            <div className="flex items-center gap-2 text-sm font-medium text-[#333333]"><Clock className="w-4 h-4 text-[#6B7280]" /> Waiting on Truva</div>
            <div className="text-xs text-[#6B7280] mt-1">{data.waiting_on_truva.length} open {data.waiting_on_truva.length === 1 ? "query" : "queries"}</div>
            {data.waiting_on_truva.slice(0, 5).map((qq) => <div key={qq.id} onClick={() => navigate(`/partner/cases/${qq.case_record_id}`)} className="text-xs mt-1 cursor-pointer hover:text-[#FF4802]"><span className="font-mono font-semibold text-[#E2521B]">{qq.case_id}</span> · {qq.text.slice(0, 80)}</div>)}
          </div>
        </div>
      )}

      {data && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="partner-filters">
          <button onClick={() => setNeeds((v) => !v)} className={pill(needs)} data-testid="partner-filter-needs">Needs my action</button>
          <span className="text-[11px] text-[#6B7280] ml-2">Stage</span>
          <button onClick={() => setStage("")} className={pill(!stage)}>All</button>
          {data.stages.map((s) => <button key={s} onClick={() => setStage(s === stage ? "" : s)} className={pill(stage === s)} data-testid={`partner-filter-stage-${s}`}>{s}</button>)}
          {data.banks.length > 0 && <span className="text-[11px] text-[#6B7280] ml-2">Bank</span>}
          {data.banks.map((b) => <button key={b} onClick={() => setBank(b === bank ? "" : b)} className={pill(bank === b)}>{b}</button>)}
        </div>
      )}

      <div className="w-full overflow-x-auto rounded-2xl border border-[#E2E2E2] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead><tr>{["Case", "Borrower", "Stage", "Bank", "Loan", "HL RM", "Pending docs", "Last update"].map((h) => (
            <th key={h} className="bg-[#F7F7F7] text-[#6B7280] font-medium text-xs py-3 px-3.5 text-left border-b border-[#E2E2E2] uppercase tracking-wider">{h}</th>))}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#FF4802] mx-auto" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#6B7280] py-10">No cases match.</td></tr>}
            {rows.map((c) => (
              <tr key={c.record_id} onClick={() => navigate(`/partner/cases/${c.record_id}`)} className={`border-b border-[#F7F7F7] hover:bg-[#F6F5EF]/70 cursor-pointer ${c.needs_my_action ? "bg-[#FF4802]/5" : ""}`} data-testid={`partner-case-${c.case_id}`}>
                <td className="py-2.5 px-3.5 whitespace-nowrap"><span className="font-mono font-semibold text-[#E2521B]">{c.case_id}</span></td>
                <td className="py-2.5 px-3.5 font-medium text-[#333333]">{c.borrower || "—"}</td>
                <td className="py-2.5 px-3.5 whitespace-nowrap"><StageChip stage={c.stage} />{c.days_in_stage != null && <span className="text-[11px] text-[#6B7280] ml-1">{c.days_in_stage}d</span>}</td>
                <td className="py-2.5 px-3.5 text-[#6B7280]">{banksLabel(c.banks)}</td>
                <td className="py-2.5 px-3.5 font-mono whitespace-nowrap">{formatINR(c.loan_amount)}</td>
                <td className="py-2.5 px-3.5 text-[#6B7280] whitespace-nowrap">{c.hl_rm || "—"}{c.hl_rm_phone && <a href={`tel:${c.hl_rm_phone}`} onClick={(e) => e.stopPropagation()} className="block text-[11px] text-[#FF4802] hover:underline">{c.hl_rm_phone}</a>}</td>
                <td className="py-2.5 px-3.5">{c.pending_docs == null ? <span className="text-[#C5C4C4]">—</span> : c.pending_docs === 0 ? <span className="text-[#159A50] text-xs">All in</span> : <span className="text-xs font-medium text-[#CA1212]">{c.pending_docs} pending</span>}</td>
                <td className="py-2.5 px-3.5 text-[#6B7280] whitespace-nowrap text-xs">{c.last_update ? fmtLastConv(c.last_update) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
